import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Download, AlertCircle, FileText, Calendar, Lock, Mail, Loader2, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert.js';

// API requests go through CloudFront
const API_BASE_URL = '';

const DownloadPage = () => {
  const { fileId: batchId } = useParams();
  const [batchInfo, setBatchInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [downloadedFiles, setDownloadedFiles] = useState(new Set());

  useEffect(() => {
    const fetchBatchInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/receive/${batchId}`);

        if (response.status === 404) {
          setError('ファイルが見つかりません。URLをご確認ください。');
          return;
        }

        if (response.status === 410) {
          setError('このファイルの有効期限が切れています。');
          return;
        }

        if (!response.ok) {
          throw new Error('ファイル情報の取得に失敗しました');
        }

        const data = await response.json();
        setBatchInfo(data);
      } catch (err) {
        console.error('Error:', err);
        setError('ファイル情報の取得に失敗しました。時間をおいて再度お試しください。');
      } finally {
        setLoading(false);
      }
    };

    fetchBatchInfo();
  }, [batchId]);

  const formatFileSize = (bytes) => {
    if (bytes == null) return 'N/A';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownload = async (targetFileId = null) => {
    if (targetFileId) {
      setDownloadingFileId(targetFileId);
    } else {
      setDownloading(true);
    }
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/receive/${batchId}/download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: batchInfo.requiresEmail ? email : undefined,
          password: batchInfo.requiresPassword ? password : undefined,
          fileId: targetFileId || undefined,
        }),
      });

      if (response.status === 403) {
        const data = await response.json();
        if (data.error === 'Invalid email address') {
          setError('メールアドレスが正しくありません。');
        } else if (data.error === 'Invalid password') {
          setError('パスワードが正しくありません。');
        } else {
          setError('認証に失敗しました。');
        }
        return;
      }

      if (response.status === 404) {
        setError('ファイルが見つかりません。');
        return;
      }

      if (response.status === 410) {
        setError('このファイルの有効期限が切れています。');
        return;
      }

      if (!response.ok) {
        throw new Error('ダウンロードに失敗しました');
      }

      const data = await response.json();

      // 複数ファイルをダウンロード
      if (data.files && data.files.length > 0) {
        for (const file of data.files) {
          if (file.downloadUrl) {
            const link = document.createElement('a');
            link.href = file.downloadUrl;
            link.download = file.fileName || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // ダウンロード済みファイルを記録
            setDownloadedFiles(prev => new Set([...prev, file.fileId]));

            // 複数ファイルの場合は少し間隔を空ける
            if (data.files.length > 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        }
      } else {
        throw new Error('ダウンロードURLが取得できませんでした');
      }
    } catch (err) {
      console.error('Download error:', err);
      setError('ダウンロードに失敗しました。時間をおいて再度お試しください。');
    } finally {
      setDownloading(false);
      setDownloadingFileId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#5a79ba]" />
      </div>
    );
  }

  const isFormValid =
    (!batchInfo?.requiresEmail || email) &&
    (!batchInfo?.requiresPassword || password);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-[800px]">
        <img
          className="mx-auto h-12 w-auto"
          src={`${process.env.PUBLIC_URL}/images/Brains_logo.png`}
          alt="Brains Logo"
        />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 tracking-tight">
          ファイルダウンロード
        </h2>
        <div className="mt-2 flex justify-center gap-8">
          <div className="text-center">
            <p className="text-sm text-gray-500">企業内全文検索エンジン</p>
            <p className="text-2xl font-bold text-[#5a79ba]">Neuron ES</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">AI異常検知ソリューション</p>
            <p className="text-2xl font-bold text-[#5a79ba]">Impulse</p>
          </div>
        </div>
      </div>

      <div className="mt-8 mx-auto w-full max-w-[800px]">
        <div className="bg-white py-8 px-4 shadow-sm border border-gray-200/60 rounded-2xl sm:px-10">
          {error ? (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : batchInfo ? (
            <>
              {/* ファイル一覧 */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span className="font-semibold">
                    {batchInfo.fileCount === 1 ? 'ファイル' : `ファイル一覧（${batchInfo.fileCount}件）`}
                  </span>
                  <span className="font-medium">{formatFileSize(batchInfo.totalSize)}</span>
                </div>

                {batchInfo.files.map((file) => (
                  <div
                    key={file.fileId}
                    className="flex items-center p-3.5 bg-gray-50/80 rounded-xl border border-gray-200/60 transition-colors duration-150 hover:bg-slate-50"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#8499c8]/12 flex items-center justify-center mr-3 flex-shrink-0">
                      <FileText className="h-5 w-5 text-[#5a79ba]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {file.fileName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.fileSize)}
                      </p>
                    </div>
                    {downloadedFiles.has(file.fileId) && (
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    )}
                    {batchInfo.fileCount > 1 && isFormValid && (
                      <button
                        onClick={() => handleDownload(file.fileId)}
                        disabled={downloadingFileId === file.fileId || downloading}
                        className="ml-2 p-2 text-[#5a79ba] hover:text-[#46639e] hover:bg-[#8499c8]/10 rounded-lg disabled:opacity-50 transition-all duration-200"
                        title="このファイルをダウンロード"
                      >
                        {downloadingFileId === file.fileId ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <Download className="h-5 w-5" />
                        )}
                      </button>
                    )}
                  </div>
                ))}

                <div className="flex items-center text-sm text-gray-500 pt-2">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>有効期限: <strong className="text-gray-700">{batchInfo.expiresAt}</strong></span>
                </div>
              </div>

              {/* 認証フォーム */}
              {batchInfo.requiresEmail && (
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1.5">
                    <Mail className="h-4 w-4 inline mr-1" />
                    メールアドレス
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="mt-1 block w-full px-3.5 py-2.5 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5a79ba]/30 focus:border-[#5a79ba] sm:text-sm transition-all duration-200"
                  />
                </div>
              )}

              {batchInfo.requiresPassword && (
                <div className="mb-6">
                  <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1.5">
                    <Lock className="h-4 w-4 inline mr-1" />
                    パスワード
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="パスワードを入力"
                    className="mt-1 block w-full px-3.5 py-2.5 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5a79ba]/30 focus:border-[#5a79ba] sm:text-sm transition-all duration-200"
                  />
                </div>
              )}

              {/* ダウンロードボタン */}
              <button
                onClick={() => handleDownload()}
                disabled={downloading || downloadingFileId || !isFormValid}
                className="w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-[#5a79ba] hover:bg-[#46639e] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5a79ba] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md"
              >
                {downloading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    ダウンロード中...
                  </>
                ) : (
                  <>
                    <Download className="h-5 w-5 mr-2" />
                    {batchInfo.fileCount === 1 ? 'ダウンロード' : `すべてダウンロード（${batchInfo.fileCount}件）`}
                  </>
                )}
              </button>

              {!batchInfo.requiresEmail && !batchInfo.requiresPassword && (
                <p className="mt-4 text-xs text-center text-gray-400">
                  このファイルは認証なしでダウンロードできます
                </p>
              )}
            </>
          ) : (
            <p className="text-center text-gray-400">ファイル情報を読み込めませんでした。</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
